import { create } from "zustand";
import {
  BusinessAddress,
  BusinessInfo,
  BusinessPosts,
  BusinessUpdateInfo,
} from "@/modules/types/business";
import { Subscribe } from "@/modules/types/subscription/subscription";
import {
  BusinessPostsResponse,
  BusinessService,
  BusinessUpdateResponse,
} from "@/modules/services/business/businessService";
import { AppError } from "@/lib/utils/errors";
import { BusinessInfoResponse } from "@/modules/shared/services/businessService";

interface BusinessInfoState {
  businessInfo: BusinessInfo | null;
  businessPosts: BusinessPosts | null;
  subscribes: Subscribe[];
  isLoadingBusiness: boolean;
  isUpdateLoading: boolean;
  isSwitchingBusiness: boolean;
  error?: string;
  updateSuccess?: boolean;
  getInfoError?: string;
}

interface BusinessInfoActions {
  getBusinessInfo: (payload?: { businessId?: string }) => Promise<void>;
  updateBusinessInfo: (payload: BusinessUpdateInfo) => Promise<void>;
  setState: (payload: Partial<BusinessInfoState>) => void;
  setBusinessInfo: (businessInfo: BusinessInfo) => void;
  setError: (error: string | undefined) => void;
  setUpdateSuccess: (success: boolean) => void;
  setGetInfoError: (error: string | undefined) => void;
  setBusinessPosts: (posts: BusinessPosts) => void;
  setSubscribes: (subscribes: Subscribe[]) => void;
  setBusinessLogo: (logo: string | undefined) => void;
  appendSubscribe: (subscribes: Subscribe[]) => void;
  setIsLoadingBusiness: (isLoading: boolean) => void;
  setUpdateLoading: (isLoading: boolean) => void;
  setBusinessAddress: (addresses: BusinessAddress[] | undefined) => void;
  appendBusinessAddress: (addresses: BusinessAddress[]) => void;
  updateBusinessImages: (images: string[]) => void;
  setIsSwitchingBusiness: (isLoading: boolean) => void;
  reset: () => void;
}

export type BusinessInfoStore = BusinessInfoState & BusinessInfoActions;

const initialState: BusinessInfoState = {
  businessInfo: null,
  businessPosts: null,
  subscribes: [],
  isLoadingBusiness: false,
  isUpdateLoading: false,
  isSwitchingBusiness: false,
};

export const useBusinessInfoStore = create<BusinessInfoStore>((set, get) => ({
  ...initialState,

  getBusinessInfo: async (payload) => {
    set({ isLoadingBusiness: true, getInfoError: undefined });
    try {
      const businessSvc = new BusinessService();
      const resp: BusinessInfoResponse = await businessSvc.getBusinessInfo(
        payload?.businessId
      );
      if (resp && resp.businessInfo) {
        const postResp: BusinessPostsResponse =
          await businessSvc.getPostsBusiness(resp.businessInfo.id);
        set({
          businessInfo: resp.businessInfo,
          businessPosts: postResp.businessPosts,
          isLoadingBusiness: false,
        });
      } else {
        const appErr = new AppError("business not found");
        appErr.logError();
        set({ getInfoError: appErr.message, isLoadingBusiness: false });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      set({ getInfoError: appErr.message, isLoadingBusiness: false });
    }
  },

  updateBusinessInfo: async (payload) => {
    set({ isUpdateLoading: true, error: undefined, updateSuccess: false });
    try {
      const svc = new BusinessService();
      const resp: BusinessUpdateResponse = await svc.updateBusinessInformation(
        payload
      );
      if (resp) {
        set((state) => ({
          businessInfo: state.businessInfo
            ? { ...state.businessInfo, ...resp.businessInfo }
            : resp.businessInfo,
          isUpdateLoading: false,
          updateSuccess: true,
          error: undefined,
        }));
      }
    } catch (error) {
      const appErr = new AppError(error);
      appErr.logError();
      set({ error: appErr.message, isUpdateLoading: false });
    }
  },

  setState: (payload) => set((state) => ({ ...state, ...payload })),
  setBusinessInfo: (businessInfo) =>
    set((state) => ({
      businessInfo: state.businessInfo
        ? { ...state.businessInfo, ...businessInfo }
        : businessInfo,
    })),
  setError: (error) => set({ error, isUpdateLoading: false }),
  setUpdateSuccess: (updateSuccess) => set({ updateSuccess }),
  setGetInfoError: (getInfoError) =>
    set({ getInfoError, isLoadingBusiness: false }),
  setBusinessPosts: (businessPosts) =>
    set((state) => ({
      businessPosts: state.businessPosts
        ? { ...state.businessPosts, ...businessPosts }
        : businessPosts,
    })),
  setSubscribes: (subscribes) => set({ subscribes }),
  setBusinessLogo: (companyLogo) =>
    set((state) => ({
      businessInfo: state.businessInfo
        ? ({ ...state.businessInfo, companyLogo } as BusinessInfo)
        : null,
    })),
  appendSubscribe: (subscribes) =>
    set((state) => ({ subscribes: [...state.subscribes, ...subscribes] })),
  setIsLoadingBusiness: (isLoadingBusiness) => set({ isLoadingBusiness }),
  setUpdateLoading: (isUpdateLoading) => set({ isUpdateLoading }),
  setBusinessAddress: (businessAddresses) =>
    set((state) => ({
      businessInfo: state.businessInfo
        ? ({ ...state.businessInfo, businessAddresses } as BusinessInfo)
        : null,
    })),
  appendBusinessAddress: (addresses) =>
    set((state) => ({
      businessInfo: state.businessInfo
        ? ({
            ...state.businessInfo,
            businessAddresses: [
              ...(state.businessInfo.businessAddresses ?? []),
              ...addresses,
            ],
          } as BusinessInfo)
        : null,
    })),
  updateBusinessImages: (companyImages) =>
    set((state) => ({
      businessInfo: state.businessInfo
        ? ({ ...state.businessInfo, companyImages } as BusinessInfo)
        : null,
    })),
  setIsSwitchingBusiness: (isSwitchingBusiness) => set({ isSwitchingBusiness }),
  reset: () => set(initialState),
}));
